import { useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { RootState, AppDispatch } from '../store'
import { 
  setConfiguracao,
  setModelos,
  setFaturamentoCompleto,
  setConfiguracoesSistema,
  setSubtitulosDisponiveis,
  setLoading,
  setError,
  resetFaturamento
} from './faturamento-slice'
import { useFaturamento } from '../../../use-cases/faturamento/useFaturamento'
import { 
  IInserirFaturamentoConfiguracaoRequest,
  IAtualizarFaturamentoConfiguracaoRequest,
  IObterFaturamentoConfiguracaoPorClienteRequest,
  IObterFaturamentoCompletoPorClienteRequest
} from '../../models'

export const useFaturamentoRedux = () => {
  const dispatch = useDispatch<AppDispatch>()
  const {
    obterConfiguracaoPorCliente,
    inserirConfiguracao,
    atualizarConfiguracao,
    obterModelos,
    obterFaturamentoCompleto,
    obterConfiguracoesSistema,
    obterSubtitulos,
  } = useFaturamento()

  // Selectors
  const configuracao = useSelector((state: RootState) => state.faturamento.configuracao)
  const modelos = useSelector((state: RootState) => state.faturamento.modelos)
  const faturamentoCompleto = useSelector((state: RootState) => state.faturamento.faturamentoCompleto)
  const configuracoesSistema = useSelector((state: RootState) => state.faturamento.configuracoesSistema)
  const descricaoPadrao = useSelector((state: RootState) => state.faturamento.descricaoPadraoNotaFiscal)
  const subtitulos = useSelector((state: RootState) => state.faturamento.subtitulosDisponiveis)
  const dataPadraoVencimento = useSelector((state: RootState) => state.faturamento.dataPadraoVencimento)
  const loading = useSelector((state: RootState) => state.faturamento.loading)
  const error = useSelector((state: RootState) => state.faturamento.error)

  // Actions
  const carregarConfiguracaoPorCliente = useCallback(async (data: IObterFaturamentoConfiguracaoPorClienteRequest) => {
    try {
      dispatch(setLoading(true))
      dispatch(setError(null))
      
      const result = await obterConfiguracaoPorCliente(data)
      if (result) {
        dispatch(setConfiguracao(result))
      }
      return result
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro ao carregar configuração'
      dispatch(setError(errorMessage))
      return null
    } finally {
      dispatch(setLoading(false))
    }
  }, [dispatch, obterConfiguracaoPorCliente])

  const carregarModelos = useCallback(async () => {
    try {
      dispatch(setLoading(true))
      dispatch(setError(null))
      
      const result = await obterModelos()
      if (result) {
        dispatch(setModelos(result))
      }
      return result
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro ao carregar modelos'
      dispatch(setError(errorMessage))
      return []
    } finally {
      dispatch(setLoading(false))
    }
  }, [dispatch, obterModelos])

  const carregarFaturamentoCompleto = useCallback(async (data: IObterFaturamentoCompletoPorClienteRequest) => {
    try {
      dispatch(setLoading(true))
      dispatch(setError(null))
      
      const result = await obterFaturamentoCompleto(data)
      if (result) {
        dispatch(setFaturamentoCompleto(result))
      }
      return result
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro ao carregar faturamento completo'
      dispatch(setError(errorMessage))
      return []
    } finally {
      dispatch(setLoading(false))
    }
  }, [dispatch, obterFaturamentoCompleto])

  const carregarConfiguracoesSistema = useCallback(async () => {
    try {
      dispatch(setLoading(true))
      dispatch(setError(null))
      
      const result = await obterConfiguracoesSistema()
      if (result) {
        dispatch(setConfiguracoesSistema(result))
      }
      return result
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro ao carregar configurações do sistema'
      dispatch(setError(errorMessage))
      return null
    } finally {
      dispatch(setLoading(false))
    }
  }, [dispatch, obterConfiguracoesSistema])

  const carregarSubtitulos = useCallback(async () => {
    try {
      dispatch(setLoading(true))
      dispatch(setError(null))
      
      const result = await obterSubtitulos()
      if (result) {
        dispatch(setSubtitulosDisponiveis(result))
      }
      return result
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro ao carregar subtítulos'
      dispatch(setError(errorMessage))
      return null
    } finally {
      dispatch(setLoading(false))
    }
  }, [dispatch, obterSubtitulos])

  const inserirConfiguracaoFaturamento = useCallback(async (data: IInserirFaturamentoConfiguracaoRequest) => {
    try {
      dispatch(setLoading(true))
      dispatch(setError(null))
      
      const result = await inserirConfiguracao(data)
      if (result) {
        dispatch(setConfiguracao(result))
      }
      return result
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro ao inserir configuração'
      dispatch(setError(errorMessage))
      return null
    } finally {
      dispatch(setLoading(false))
    }
  }, [dispatch, inserirConfiguracao])

  const atualizarConfiguracaoFaturamento = useCallback(async (data: IAtualizarFaturamentoConfiguracaoRequest) => {
    try {
      dispatch(setLoading(true))
      dispatch(setError(null))
      
      const result = await atualizarConfiguracao(data)
      if (result) {
        dispatch(setConfiguracao(result))
      }
      return result
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro ao atualizar configuração'
      dispatch(setError(errorMessage))
      return null
    } finally {
      dispatch(setLoading(false))
    }
  }, [dispatch, atualizarConfiguracao])

  const limparErro = useCallback(() => {
    dispatch(setError(null))
  }, [dispatch])

  const resetarFaturamento = useCallback(() => {
    dispatch(resetFaturamento())
  }, [dispatch])

  return {
    // Estados
    configuracao,
    modelos,
    faturamentoCompleto,
    configuracoesSistema,
    descricaoPadrao,
    subtitulos,
    dataPadraoVencimento,
    loading,
    error,
    
    // Actions
    carregarConfiguracaoPorCliente,
    carregarModelos,
    carregarFaturamentoCompleto,
    carregarConfiguracoesSistema,
    carregarSubtitulos,
    inserirConfiguracaoFaturamento,
    atualizarConfiguracaoFaturamento,
    
    // Utilitários
    limparErro,
    resetarFaturamento
  }
}
